import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { CurrencyCode } from '../types';
import { USD_RATES } from '../lib/currencies';

interface CurrencyContextValue {
  /** User's display/base currency. */
  base: CurrencyCode;
  setBase: (code: CurrencyCode) => void;
  convert: (amount: number, from: CurrencyCode, to: CurrencyCode) => number;
  rate: (from: CurrencyCode, to: CurrencyCode) => number;
}

const CurrencyContext = createContext<CurrencyContextValue | undefined>(undefined);

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [base, setBase] = useState<CurrencyCode>('USD');

  const rate = useCallback((from: CurrencyCode, to: CurrencyCode) => USD_RATES[to] / USD_RATES[from], []);
  const convert = useCallback(
    (amount: number, from: CurrencyCode, to: CurrencyCode) => Math.round(amount * rate(from, to) * 1e6) / 1e6,
    [rate],
  );

  const value = useMemo<CurrencyContextValue>(() => ({ base, setBase, convert, rate }), [base, convert, rate]);
  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrencyContext() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrencyContext must be used within a CurrencyProvider');
  return ctx;
}
